import { createApiClient } from './api/client.js';
import { createDeviceMatcher, detectDeviceClass, readDeviceClassInputs } from './lib/device.js';
import { isCrossOriginIsolated, readRuntimeEnv } from './lib/env.js';
import { isEmbedded } from './lib/iframe.js';
import { shouldWarnMobileSize } from './loader/mobile-guard.js';
import { createOrchestrator } from './orchestrator/orchestrator.js';
import { createAppStore } from './state/store.js';
import { showMobileSizeWarningModal } from './ui/components/mobile-size-warning-modal.js';
import { createRenderer } from './ui/render.js';
import { SparkViewerAdapter } from './viewer/spark-adapter.js';

function showIsolationOverlay(): void {
  const overlay = document.createElement('div');
  overlay.id = 'oniri-isolation-error';
  overlay.setAttribute('role', 'alert');
  overlay.style.cssText =
    'position:fixed;inset:0;background:#7f1d1d;color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:system-ui,sans-serif;text-align:center;padding:2rem;z-index:9999';
  overlay.innerHTML = `
    <h1 style="font-size:1.5rem;font-weight:600;margin-bottom:1rem">Configurazione header non valida</h1>
    <p style="max-width:32rem;line-height:1.5">Il viewer richiede headers COOP/COEP per abilitare SharedArrayBuffer (rendering Gaussian Splatting). Verifica che il deploy serva i corretti header <code>Cross-Origin-Opener-Policy: same-origin</code> e <code>Cross-Origin-Embedder-Policy: require-corp</code>.</p>
    <button type="button" id="oniri-isolation-dismiss" style="margin-top:1.5rem;padding:0.5rem 1rem;background:#fff;color:#7f1d1d;border:0;border-radius:0.375rem;cursor:pointer;font-weight:500">Continua comunque</button>
  `;
  document.body.appendChild(overlay);
  document.getElementById('oniri-isolation-dismiss')?.addEventListener('click', () => {
    overlay.remove();
  });
}

interface Layers {
  canvas: HTMLCanvasElement;
  uiRoot: HTMLElement;
}

function ensureLayers(app: HTMLElement): Layers {
  app.innerHTML = '';

  const canvasLayer = document.createElement('div');
  canvasLayer.id = 'viewer-canvas-layer';
  canvasLayer.style.cssText = 'position:fixed;inset:0;z-index:0';

  const canvas = document.createElement('canvas');
  canvas.id = 'viewer-canvas';
  canvas.style.cssText = 'display:block;width:100vw;height:100vh';
  canvasLayer.appendChild(canvas);

  const uiRoot = document.createElement('div');
  uiRoot.id = 'viewer-ui-root';
  // pointer-events:none lets orbit drag pass through; CTAs re-enable via class.
  uiRoot.style.cssText = 'position:fixed;inset:0;z-index:10;pointer-events:none';

  app.appendChild(canvasLayer);
  app.appendChild(uiRoot);

  return { canvas, uiRoot };
}

async function bootstrap(): Promise<void> {
  const env = readRuntimeEnv('main');
  console.warn('[oniri-viewer] mode=main env=', env);

  if (!isCrossOriginIsolated()) {
    showIsolationOverlay();
  }

  if (isEmbedded(window.location.href)) {
    console.warn(
      '[oniri-viewer] / served inside iframe — frame-ancestors header should have blocked it; check deploy',
    );
  }

  const root = document.getElementById('app');
  if (!root) {
    console.error('[oniri-viewer] missing #app root');
    return;
  }

  const { canvas, uiRoot } = ensureLayers(root);
  const adapter = new SparkViewerAdapter();
  const apiClient = createApiClient();
  const store = createAppStore();
  // 4.M: device matcher owned by bootstrap. Re-broadcast through the
  // orchestrator's 'device-state' event keeps the renderer single-
  // channel; the body[data-pointer-coarse] attribute is set here so
  // tap-target CSS rules apply globally.
  const deviceMatcher = createDeviceMatcher();
  document.body.dataset['pointerCoarse'] = String(deviceMatcher.snapshot.pointerCoarse);
  const bodyDatasetUnsub = deviceMatcher.subscribe((next) => {
    document.body.dataset['pointerCoarse'] = String(next.pointerCoarse);
  });

  // Step 7 Phase 0 — mobile OOM soft-warning hook. Called by orchestrator
  // after fetchMeta success and before dispatch META_FETCHED. Returns
  // false if the user declined → orchestrator dispatches LOAD_CANCELLED_BY_USER.
  // Snapshot device class once at hook-call time (not at bootstrap):
  // user could have rotated tablet between bootstrap and meta resolve,
  // and matchMedia matches reflect the live state.
  const orchestrator = createOrchestrator({
    apiClient,
    adapter,
    store,
    canvas,
    href: window.location.href,
    embed: false,
    deviceMatcher,
    shouldProceedAfterMeta: async (meta) => {
      const inputs = readDeviceClassInputs(deviceMatcher.snapshot, window);
      const deviceClass = detectDeviceClass(inputs);
      if (!shouldWarnMobileSize(deviceClass, meta)) {
        return true; // not warnable — proceed normally
      }
      // Mobile + raw .ply > 150 MB: ask user. Modal ESC / "Copy link"
      // resolve false; "Continua comunque" resolves true.
      return showMobileSizeWarningModal(uiRoot, {
        meta,
        shareUrl: window.location.href,
      });
    },
  });

  const renderer = createRenderer({
    uiRoot,
    store,
    onRetry: () => store.dispatch({ type: 'RETRY' }),
    embed: false,
    orchestratorEvents: orchestrator.events,
    getSortedPresets: () => orchestrator.getSortedPresets(),
    getTourSnapshot: () => orchestrator.getTourSnapshot(),
    getSortedHotspots: () => orchestrator.getSortedHotspots(),
    getHotspotsSnapshot: () => orchestrator.getHotspotsSnapshot(),
    getFloorplan: () => orchestrator.getFloorplan(),
    getFloorplanSnapshot: () => orchestrator.getFloorplanSnapshot(),
    getMeasurementsSnapshot: () => orchestrator.getMeasurementsSnapshot(),
    getRaycastAvailable: () => orchestrator.getRaycastAvailable(),
    getWalkSnapshot: () => orchestrator.getWalkSnapshot(),
    getXRSnapshot: () => orchestrator.getXRSnapshot(),
    getDeviceCapabilities: () => orchestrator.getDeviceCapabilities(),
    loadedCallbacks: {
      onScreenshot: () => {
        void orchestrator.takeScreenshot();
      },
      onReset: () => orchestrator.resetView(),
      onFullscreenToggle: () => {
        void orchestrator.toggleFullscreen();
      },
      onPerfToggle: () => orchestrator.togglePerfHud(),
      onShare: () => {
        void orchestrator.shareUrl();
      },
      onApplyPreset: (presetId) => {
        void orchestrator.applyPreset(presetId);
      },
      onTourStart: () => orchestrator.startTour(),
      onTourPauseResume: () => {
        const snap = orchestrator.getTourSnapshot();
        if (snap.kind === 'paused') orchestrator.resumeTour();
        else orchestrator.pauseTour();
      },
      onHotspotOpen: (id) => orchestrator.openHotspot(id),
      onClose: () => orchestrator.closeHotspot(),
      onPrev: () => orchestrator.prevHotspot(),
      onNext: () => orchestrator.nextHotspot(),
      onFloorplanClose: () => orchestrator.closeFloorplan(),
      onFloorplanTeleport: (pixelXY) => orchestrator.teleportOnFloorplanPixel(pixelXY),
      onFloorplanImageError: () => orchestrator.reportFloorplanImageError(),
      onMeasureReset: () => orchestrator.resetMeasurements(),
      onMeasureSetUnits: (units) => orchestrator.setMeasurementUnits(units),
      onMeasureRemoveLast: () => orchestrator.removeLastMeasurePoint(),
      onOpenXR: () => orchestrator.openXRModal(),
      onXRClose: () => orchestrator.closeXRModal(),
      onEnterVR: () => orchestrator.enterXR('vr'),
      onEnterAR: () => orchestrator.enterXR('ar'),
      onWalkToggle: () => orchestrator.toggleWalk(),
      onMeasureToggle: () => orchestrator.toggleMeasure(),
      onUnavailableFeature: (reason) => orchestrator.notifyUnavailableFeature(reason),
    },
  });
  renderer.start();

  adapter.events.addEventListener('context-lost', () => {
    console.warn('[oniri-viewer] WebGL context lost — reload the page');
  });

  window.addEventListener(
    'pagehide',
    () => {
      // 4.M cleanup ordering: bodyDataset sub → renderer (which has
      // its own device-state listener) → orchestrator (which holds
      // the matcher subscription) → matcher itself.
      bodyDatasetUnsub();
      renderer.dispose();
      void orchestrator.dispose();
      deviceMatcher.dispose();
    },
    { once: true },
  );

  await orchestrator.start();
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      void bootstrap();
    },
    { once: true },
  );
} else {
  void bootstrap();
}
